import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Layouts from "../../config/layouts/Layouts";
import NavbarComponent from "../../components/navbar/Navbar";
import Footer from "../../components/footer/Footer";
import {
    PROYECTOS,
    CASOS_EXITO,
    FILTROS_CASOS,
} from "../../data/proyectosData";
import { MotionDiv, StaggerGrid, StaggerItem, fadeInUp } from "../../utils/animations";
import "../../components/proyectos/proyectos.css";

function CasoImagen({ src, alt }) {
    const { t } = useTranslation();
    const [failed, setFailed] = useState(false);

    if (failed) {
        return (
            <div className="proyectos-caso-img-fallback" aria-hidden>
                {t("proyectos.imgNotAvailable")}
            </div>
        );
    }

    return (
        <img
            className="proyectos-caso-img"
            src={src}
            alt={alt}
            loading="lazy"
            decoding="async"
            onError={() => setFailed(true)}
        />
    );
}

function Proyectos() {
    const { t } = useTranslation();
    const [filtro, setFiltro] = useState("All");

    const casosFiltrados = useMemo(() => {
        if (filtro === "All") return CASOS_EXITO;
        return CASOS_EXITO.filter((c) => c.categoria === filtro);
    }, [filtro]);

    return (
        <Layouts>
            <header className="shrink-0">
                <NavbarComponent />
            </header>

            <main className="proyectos-page">
                <div className="proyectos-container">
                    <MotionDiv className="proyectos-hero">
                        <h1 className="proyectos-title">{t("proyectos.title")}</h1>
                        <p className="proyectos-subtitle">
                            {t("proyectos.subtitle")}
                        </p>
                    </MotionDiv>

                    <MotionDiv delay={0.1}>
                        <h2 className="proyectos-section-title">{t("proyectos.appsTitle")}</h2>
                    </MotionDiv>
                    <StaggerGrid className="proyectos-apps-grid">
                        {PROYECTOS.map((p) => (
                            <StaggerItem key={p.key}>
                                <motion.article
                                    className="proyectos-app-card"
                                    whileHover={{ y: -6, scale: 1.01 }}
                                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                                >
                                    <div className="proyectos-app-img-wrap">
                                        <img
                                            className="proyectos-app-img"
                                            src={p.imagen}
                                            alt=""
                                            loading="lazy"
                                            decoding="async"
                                        />
                                    </div>
                                    <div className="proyectos-app-body">
                                        <span className="proyectos-app-tipo">{t(`proyectos.items.${p.key}.tipo`)}</span>
                                        <h3 className="proyectos-app-nombre">{t(`proyectos.items.${p.key}.nombre`)}</h3>
                                        <p className="proyectos-app-desc">{t(`proyectos.items.${p.key}.descripcion`)}</p>
                                        {p.casePath ? (
                                            <Link className="proyectos-app-link" to={p.casePath}>{t("proyectos.viewCaseStudy")}</Link>
                                        ) : null}
                                        {p.urlWeb ? (
                                            <a
                                                className="proyectos-app-link"
                                                href={p.urlWeb}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {t("proyectos.viewWebsite")}
                                            </a>
                                        ) : null}
                                        {p.url ? (
                                            <a
                                                className="proyectos-app-link"
                                                href={p.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {t("proyectos.viewPlayStore")}
                                            </a>
                                        ) : null}
                                    </div>
                                </motion.article>
                            </StaggerItem>
                        ))}
                    </StaggerGrid>

                    <MotionDiv className="mt-8 rounded-2xl border border-em-text/10 bg-em-card p-6 md:p-8" delay={0.12}>
                        <p className="text-xs font-bold uppercase tracking-[0.18em] text-em-accent2">
                            {t("proyectos.aiProductTag")}
                        </p>
                        <div className="mt-3 grid gap-5 md:grid-cols-[1fr_auto] md:items-end">
                            <div>
                                <h2 className="text-2xl font-black text-em-text">
                                    {t("proyectos.aiProductTitle")}
                                </h2>
                                <p className="mt-2 max-w-3xl leading-7 text-em-soft">
                                    {t("proyectos.aiProductDesc")}
                                </p>
                            </div>
                            <a
                                className="proyectos-app-link"
                                href="https://play.google.com/store/apps/details?id=com.emmpieza.radarpresidencial"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {t("proyectos.viewGooglePlay")}
                            </a>
                            <Link className="proyectos-app-link" to="/casos/analisis-presidencial-colombia-2026">
                                {t("proyectos.viewCaseStudy")}
                            </Link>
                        </div>
                    </MotionDiv>

                    <MotionDiv delay={0.15}>
                        <h2 className="proyectos-section-title">{t("proyectos.successCasesTitle")}</h2>
                    </MotionDiv>
                    <motion.div
                        className="proyectos-filters"
                        role="tablist"
                        aria-label={t("proyectos.filterAria")}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeInUp}
                    >
                        {FILTROS_CASOS.map((f) => (
                            <button
                                key={f}
                                type="button"
                                role="tab"
                                aria-selected={filtro === f}
                                className={`proyectos-filter-btn ${filtro === f ? "is-active" : ""}`}
                                onClick={() => setFiltro(f)}
                            >
                                {f === "All" ? t("proyectos.filterAll") : f}
                            </button>
                        ))}
                    </motion.div>

                    <StaggerGrid className="proyectos-casos-grid" key={filtro}>
                        {casosFiltrados.length === 0 ? (
                            <p className="proyectos-empty">{t("proyectos.noResults")}</p>
                        ) : (
                            casosFiltrados.map((c) => (
                                <StaggerItem key={`${c.key}-${c.categoria_tag}`}>
                                    <motion.article
                                        className="proyectos-caso-card"
                                        whileHover={{ y: -6 }}
                                        transition={{ type: "spring", stiffness: 300, damping: 22 }}
                                    >
                                        <div className="proyectos-caso-img-wrap">
                                            <CasoImagen src={c.imagen} alt={t(`proyectos.casos.${c.key}.titulo`)} />
                                        </div>
                                        <div className="proyectos-caso-body">
                                            <div className="proyectos-caso-tags">
                                                <span className="proyectos-caso-cat">{c.categoria_tag}</span>
                                                <span className="proyectos-caso-metrica">
                                                    <strong>{t(`proyectos.casos.${c.key}.metrica`)}</strong>
                                                    {" · "}
                                                    {t(`proyectos.casos.${c.key}.metricaEtiqueta`)}
                                                </span>
                                            </div>
                                            <h3 className="proyectos-caso-titulo">{t(`proyectos.casos.${c.key}.titulo`)}</h3>
                                            <dl className="proyectos-caso-block">
                                                <dt>{t("proyectos.retoLabel")}</dt>
                                                <dd>{t(`proyectos.casos.${c.key}.reto`)}</dd>
                                            </dl>
                                            <dl className="proyectos-caso-block">
                                                <dt>{t("proyectos.solucionLabel")}</dt>
                                                <dd>{t(`proyectos.casos.${c.key}.solucion`)}</dd>
                                            </dl>
                                            <div className="proyectos-caso-actions">
                                                {c.casePath ? (
                                                    <Link className="proyectos-caso-link proyectos-caso-link--outline" to={c.casePath}>
                                                        {t("proyectos.caseStudyLink")}
                                                    </Link>
                                                ) : null}
                                                {c.urlandroid && c.urlios ? (
                                                    <>
                                                        <a
                                                            className="proyectos-caso-link"
                                                            href={c.urlandroid}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            {t("proyectos.android")}
                                                        </a>
                                                        <a
                                                            className="proyectos-caso-link proyectos-caso-link--outline"
                                                            href={c.urlios}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            {t("proyectos.ios")}
                                                        </a>
                                                    </>
                                                ) : (
                                                    <a
                                                        className="proyectos-caso-link"
                                                        href={c.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {t("proyectos.viewProject")}
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    </motion.article>
                                </StaggerItem>
                            ))
                        )}
                    </StaggerGrid>
                </div>
            </main>

            <Footer />
        </Layouts>
    );
}

export default Proyectos;
